import { Component, OnInit, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router, RouterLink } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { ILoginUser_Request } from "../../../Utils/_services/account/account.model";
import { IApiError } from "../../../Utils/_interceptors/network-error.model";
import { AccountService } from "../../../Utils/_services/account/account.service";
import { finalize } from "rxjs";
import { LeftUiTemplateComponent } from "../left-ui-template/left-ui-template.component";
import { environment } from "../../../environments/environment";
import { SessionService } from "../../../Utils/_services/session/session.service";

@Component({
  selector: "app-login",
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, LeftUiTemplateComponent],
  templateUrl: "./login.component.html",
  styleUrl: "./login.component.css",
})
export class LoginComponent implements OnInit {
  loginModel: ILoginUser_Request = {
    email: "",
    password: "",
    loginType: "email",
  };
  isLoading = signal(false);
  showPassword = false;
  errorMessage = "";
  rememberMe = false;
  year = signal(new Date().getFullYear());
  pwaUrl: string = `${environment.pwaFrontendBaseUrl}/auth/signup`;

  constructor(
    private accountService: AccountService,
    private sessionService: SessionService,
    private router: Router,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    const savedCredentials = localStorage.getItem("rememberedCredentials");
    if (savedCredentials) {
      const credentials = JSON.parse(savedCredentials);
      this.loginModel.email = credentials.email;
      this.rememberMe = true;
    }
  }

  onSubmit(): void {
    if (this.isLoading()) return;

    if (!this.loginModel.email || !this.loginModel.password) {
      this.errorMessage = "Please enter both username/email and password.";
      return;
    }

    this.isLoading.set(true);
    this.errorMessage = "";
    sessionStorage.setItem('rememberMe', JSON.stringify(this.rememberMe));
    this.accountService
      .login(this.loginModel, this.rememberMe)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        })
      )
      .subscribe({
        next: response => {
          if (response.auth.success) {
            if (response.data?.requiresAdminVerification) {
              this.toastr.info(response.msg);
              this.router.navigate(["/auth/admin-verify-otp"], {
                state: {
                  email: this.loginModel.email,
                  resetExpireTime: response.data.resetExpireTime
                }
              });
              return;
            }

            if (this.rememberMe) {
              localStorage.setItem("rememberedCredentials", JSON.stringify({ email: this.loginModel.email }));
            } else {
              localStorage.removeItem("rememberedCredentials");
            }

            this.toastr.success("Logged in successfully");

            if (response.data.role === "admin" || response.data.role === "super_admin") {
              this.router.navigate(["/admin/dashboard"]);
            } else {
              const userData = this.sessionService.getCurrentUser();
              if (userData) {
                this.sessionService.clearSession();
                localStorage.removeItem("user");
                const adoreTheBallonUrlPlusJwt = `${environment.pwaFrontendBaseUrl}/auth/login?id=${encodeURIComponent(userData.uuidv4Id)}`;
                window.open(adoreTheBallonUrlPlusJwt, "_self");
              }
            }
          } else {
            this.toastr.error(response.msg);
          }
        },
        error: (error: IApiError) => {
          this.errorMessage = error.message;
        },
      });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  navigateToRegister(): void {
    this.router.navigate(["/home/register"]);
  }

  navigateToForgotPassword(): void {
    this.router.navigate(["/forgot"]);
  }

  clearError(): void {
    this.errorMessage = "";
  }
}
